import api from '../config/api';

/**
 * Authentication Service
 * Handles all authentication-related API calls
 */

/**
 * Login user
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise} API response with user data and token
 */
export const login = async (email, password) => {
  try {
    const response = await api.post('/api/auth/login', {
      email,
      password,
    });
    // Store token if provided in response
    if (response.data.data.token) {
      localStorage.setItem('authToken', response.data.data.token);
    }
    
    // Store user data if provided
    if (response.data.data.user) {
      localStorage.setItem('userData', JSON.stringify(response.data.data.user));
    }
    
    return response.data.data;
  } catch (error) {
    // Extract error message from response
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Login failed. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Logout user
 * Clears local storage and token
 */
export const logout = () => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('userData');
  localStorage.removeItem('isAuthenticated');
  localStorage.removeItem('customerAuthenticated');
  localStorage.removeItem('agencyAuthenticated');
};

/**
 * Get current user data from localStorage
 * @returns {Object|null} User data or null
 */
export const getCurrentUser = () => {
  const userData = localStorage.getItem('userData');
  return userData ? JSON.parse(userData) : null;
};

/**
 * Get auth token from localStorage
 * @returns {string|null} Auth token or null
 */
export const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

/**
 * Check if user is authenticated
 * @returns {boolean} True if token exists
 */
export const isAuthenticated = () => {
  return !!localStorage.getItem('authToken');
};

/**
 * Send OTP to email for password reset
 * @param {string} email - User email address
 * @returns {Promise} API response
 */
export const forgotPassword = async (email) => {
  try {
    const response = await api.post('/api/auth/forgot-password', {
      email,
    });
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to send OTP. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Verify OTP for password reset
 * @param {string} email - User email address
 * @param {string} otp - 6-digit OTP code
 * @returns {Promise} API response with reset token
 */
export const verifyOTP = async (email, otp) => {
  try {
    const response = await api.post('/api/auth/verify-otp', {
      email,
      otp,
    });
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Invalid OTP. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Reset password after OTP verification
 * @param {string} email - User email address
 * @param {string} otp - Verified OTP code
 * @param {string} newPassword - New password
 * @returns {Promise} API response
 */
export const resetPassword = async (email, otp, newPassword) => {
  try {
    const response = await api.post('/api/auth/reset-password', {
      email,
      otp,
      newPassword,
    });
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to reset password. Please try again.';
    throw new Error(errorMessage);
  }
};

